/**
 * FilterDialog Component
 * 
 * Modal dialog for customizing activity filter preferences.
 * Opens as an overlay on top of the home screen with backdrop blur.
 * 
 * Design: Based on docs/references/mockups/home-v2/dialog.svg
 */

import { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HOME_V2_COLORS } from '../colors';
import FilterDialogHeader from './FilterDialogHeader';
import FilterDialogFooter from './FilterDialogFooter';
import LocationFilter from './LocationFilter';
import TimeFilter from './TimeFilter';
import KidsCountFilter from './KidsCountFilter';
import AgeRangeFilter from './AgeRangeFilter';
import { AgeRangeOption } from '../../../contexts/FilterContext';

export const getAgeRange = (ageRanges: AgeRangeOption[]) => {

  const ageRangeData = ageRanges.length > 0
    ? {
      min: Math.min(...ageRanges.map(r => parseInt(r.split(' - ')[0]))),
      max: Math.max(...ageRanges.map(r => parseInt(r.split(' - ')[1]))),
    }
    : { min: 2, max: 5 }; // Default full range if none selected

    return ageRangeData
};




interface FilterDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FilterDialog({ isOpen, onClose }: FilterDialogProps) {
  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when dialog is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40"
            style={{
              backgroundColor: HOME_V2_COLORS.dialogOverlay,
              //backdropFilter: 'blur(2px)',
              //WebkitBackdropFilter: 'blur(10px)',
            }}
            onClick={handleBackdropClick}
            aria-hidden="true"
          />

          {/* Dialog Container - Responsive Bottom Sheet (Mobile) / Modal (Desktop) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-x-0 bottom-0 z-50 md:inset-0 md:flex md:items-center md:justify-center"
            style={{
              top: '20vh',
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="filter-dialog-title"
          >
            <div
              className="h-full rounded-t-3xl shadow-xl overflow-hidden flex flex-col md:h-auto md:max-h-[80vh] md:w-[448px] md:rounded-2xl"
              style={{
                backgroundColor: HOME_V2_COLORS.dialogBackground,
              }}
            >
              {/* Header */}
              <FilterDialogHeader onClose={onClose} />

              {/* Instructional Text */}
              <div className="px-6 pb-4">
                <p
                  className="text-sm leading-none"
                  style={{ 
                    color: HOME_V2_COLORS.dialogBodyText,
                    lineHeight: 'normal',
                  }}
                >
                  Each time you share your preferences, we&apos;ll tailor the experience just for you.
                </p>
              </div>

              {/* Scrollable Content Area */}
              <div className="flex-1 overflow-y-auto px-6 pb-6">
                <div className="space-y-6">
                  {/* Location Filter */}
                  <LocationFilter />
                  
                  {/* Time Filter */}
                  <TimeFilter />
                  
                  {/* Kids Count Filter */}
                  <KidsCountFilter />
                  
                  {/* Age Range Slider (replaces AgeRangeFilter) */}
                  <AgeRangeFilter />
                </div>
              </div>

              {/* Footer with Action Buttons */}
              <FilterDialogFooter onClose={onClose} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
